import { Button, Flex, Stack } from '@chakra-ui/react';
import Link from 'components/Link';
import { MultiSelectRolesDropDown } from 'components/Table/MultiSelectRolesDropDown';
import useOPUsersQuery from 'hooks/useOPUsersQuery';
import { isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { IdentificationResultIcon } from '../../components/Icons';
import { DataTable } from '../../components/Table/DataTable';
import { ContentCard } from '../../layout/ContentCard';

const UserOverviewCard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const initialFilters = {
    searchString: '',
    itemsPerPage: 20,
    roles: [],
  };
  const localStorageData = window.localStorage.getItem('user_filters');
  // @ts-ignore
  const destringifiedData = JSON.parse(localStorageData) || {};
  useEffect(() => {
    if (!localStorageData) {
      window.localStorage.setItem('user_filters', JSON.stringify(initialFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchString, setSearchString] = useState(destringifiedData?.searchString || initialFilters?.searchString);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(destringifiedData?.itemsPerPage || initialFilters?.itemsPerPage);
  const [roles, setRoles] = useState(destringifiedData?.roles || initialFilters?.roles);

  const [filtersSelected, setFiltersSelected] = useState(false);

  const opUsersQuery = useOPUsersQuery({
    searchString,
    page,
    itemsPerPage,
    roles,
  });

  const initialLoad = searchString === '' && itemsPerPage === 20 && page === 1 && isNil(roles);

  useEffect(() => {
    setPage(1);
  }, [searchString, itemsPerPage, roles]);

  useEffect(() => {
    setFiltersSelected(roles?.length !== 0);

    window.localStorage.setItem(
      'user_filters',
      JSON.stringify({
        searchString,
        roles,
        itemsPerPage,
      }),
    );
  }, [searchString, itemsPerPage, roles]);

  const columns = useMemo(
    () => [
      {
        Header: t('user:id'),
        accessor: 'id',
      },
      {
        Header: t('user:name'),
        accessor: 'name',
      },

      {
        Header: t('user:email'),
        accessor: 'email',
      },
      {
        Header: t('user:roles'),
        accessor: (row: any) =>
          row.roles?.map((role: { label: string; name: string }, index: number) => {
            return <div key={index}>{role?.label}</div>;
          }),

        shouldFilter: true,
        Filter: () =>
          MultiSelectRolesDropDown({
            roles,
            setRoles,
            allRolesSelected: false,
          }),
      },

      {
        id: 'button',
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return (
            <Flex alignContent={'flex-end'}>
              <Button
                ml={'auto'}
                onClick={() => {
                  navigate(`/app/op/user/${row.original.id}`);
                }}
              >
                {t('common:details')}
              </Button>
            </Flex>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchString, roles, page, opUsersQuery.data, itemsPerPage],
  );

  return (
    <ContentCard icon={<IdentificationResultIcon boxSize={10} />} minH="fit-content" header={t('pages:user.table.header')}>
      <Button as={Link} variant={'primary'} ml={5} mt={10} to={'/app/op/user/create'}>
        {t('user:invite')}
      </Button>
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w={'100%'}>
          <DataTable
            resetFilter={() => {
              setSearchString(initialFilters?.searchString);
              setPage(1);
              setRoles(initialFilters?.roles);
              setItemsPerPage(initialFilters?.itemsPerPage);
              window.localStorage.setItem('announcement_filters', JSON.stringify(initialFilters));
            }}
            data={(opUsersQuery.data?.data as any[]) || []}
            globalFilter={searchString}
            filtersSelected={filtersSelected}
            // @ts-ignore
            setGlobalFilter={setSearchString}
            autoFocus={true}
            globalFilterPlaceholder={t('pages:user.table.search')}
            // @ts-ignore
            hasFilters={true}
            hasSearchInput={true}
            currentPage={opUsersQuery?.data?.meta?.current_page ? opUsersQuery?.data?.meta?.current_page - 1 : 0}
            perPage={itemsPerPage}
            total={opUsersQuery.data?.meta?.last_page}
            columns={columns}
            setPage={setPage}
            setPerPage={setItemsPerPage}
            loading={opUsersQuery.isLoading || opUsersQuery.isFetching}
            isMoreThenOnePage={!initialLoad || Boolean(opUsersQuery.data?.meta?.next_page_url)}
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default UserOverviewCard;
